import { env } from '../config/env';
import { supabase } from './supabase';

export interface ApiError extends Error {
  code: string;
  status: number;
  detail?: Record<string, unknown>;
}

interface ApiErrorBody {
  code: string;
  message: string;
  detail?: Record<string, unknown>;
}

async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

async function parseError(response: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  let parsed: ApiErrorBody;
  if (isRecord(body) && typeof body.code === 'string' && typeof body.message === 'string') {
    parsed = body as unknown as ApiErrorBody;
  } else {
    parsed = {
      code: 'HTTP_ERROR',
      message: `HTTP ${response.status} ${response.statusText}`,
    };
  }

  const err = new Error(parsed.message) as ApiError;
  err.code = parsed.code;
  err.status = response.status;
  err.detail = parsed.detail;
  return err;
}

async function request<T>(
  method: string,
  path: string,
  options: { body?: unknown; query?: Record<string, unknown>; signal?: AbortSignal } = {},
): Promise<T> {
  const url = new URL(path.replace(/^\//u, ''), `${env.VITE_API_URL.replace(/\/$/u, '')}/`);
  if (options.query) {
    for (const [k, v] of Object.entries(options.query)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
    }
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(await authHeader()),
  };

  const response = await fetch(url.toString(), {
    method,
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
    credentials: 'omit',
  });

  if (!response.ok) throw await parseError(response);

  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return undefined as T;

  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, query?: Record<string, unknown>, signal?: AbortSignal) =>
    request<T>('GET', path, { query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body }),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, { body }),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, { body }),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
